import { format, parseISO } from 'date-fns';
import { MoreVertical } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Card,
  CardContent,
  CardHeader,
} from '@/modules/ui/components/card/Card';
import { ConfirmDeleteDialog } from '@/modules/ui/components/confirm-delete-dialog/ConfirmDeleteDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/modules/ui/components/dropdown-menu/DropdownMenu';

import { type NoteFormData, type NoteTypeOption } from './NoteForm';
import { NoteForm } from './NoteForm';
import { NoteText } from './NoteText';

type Note = {
  id: string;
  createdAt: string;
  updatedAt?: string | null;
  type: string;
  content: string;
};

interface NoteCardProps<TNote extends Note> {
  note: TNote;
  onUpdate: (data: NoteFormData) => void;
  onDelete: (noteId: string) => void;
  isUpdating?: boolean;
  typeOptions: readonly NoteTypeOption[];
  defaultType?: string;
  canEdit?: boolean;
  editMenuLabel?: string;
  author?: string | null;
  displayContent?: string;
  headerControls?: ReactNode;
  contentControls?: ReactNode;
  contentFooter?: ReactNode;
}

export const NoteCard = <TNote extends Note>({
  note,
  onUpdate,
  onDelete,
  isUpdating = false,
  typeOptions,
  defaultType,
  canEdit = true,
  editMenuLabel = 'Edit',
  author,
  displayContent,
  headerControls,
  contentControls,
  contentFooter,
}: NoteCardProps<TNote>) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleSaveEdit = (data: NoteFormData) => {
    onUpdate(data);
    setIsEditing(false);
  };

  const handleDelete = () => {
    onDelete(note.id);
    setIsDeleteDialogOpen(false);
  };

  const hasHeaderActions = Boolean(headerControls) || canEdit;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div className="flex-1 space-y-0.5">
            <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <span>
                {format(parseISO(note.createdAt), 'MMM d, yyyy h:mm a')}
              </span>
              {note.updatedAt && note.updatedAt !== note.createdAt && (
                <>
                  <span>·</span>
                  <span>edited</span>
                </>
              )}
              <span>·</span>
              <Badge variant="secondary">{note.type}</Badge>
            </div>
            {author && (
              <p className="text-muted-foreground text-xs">by {author}</p>
            )}
          </div>
          {!isEditing && hasHeaderActions && (
            <div className="flex items-center gap-2">
              {headerControls}
              {canEdit && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleEdit}>
                      {editMenuLabel}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setIsDeleteDialogOpen(true)}
                      className="text-destructive"
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <NoteForm
              onSubmit={handleSaveEdit}
              onCancel={handleCancelEdit}
              defaultValues={{
                content: note.content,
                type: defaultType ?? note.type,
              }}
              isLoading={isUpdating}
              submitLabel="Save"
              typeOptions={typeOptions}
              defaultType={defaultType}
            />
          ) : (
            <div className="space-y-3">
              {contentControls}
              <NoteText text={displayContent ?? note.content} />
              {contentFooter}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDeleteDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleDelete}
        title="Delete Note"
        description="Are you sure you want to delete this note? This action cannot be undone."
      />
    </>
  );
};
